import { useState } from "react";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { MarketingFooter } from "@/components/marketing/MarketingSections2";
import { Link } from "react-router-dom";
import { ChevronDown, HardHat, Search } from "lucide-react";
import { SEO } from "@/lib/seo";
import { SIGNUP_FAQ } from "@/data/marketing";

// Comprehensive FAQ. Each entry is a Q + A pair. The answer text is also
// emitted as FAQPage JSON-LD so Google can show these as rich snippets
// directly under the search result ("People also ask").
const FAQS = [
  { cat: "Pricing & trial", ...SIGNUP_FAQ },
  {
    cat: "Pricing & trial",
    q: "How much does Fieldlink cost?",
    a: "Fieldlink starts at NZ$39.99/month for crews up to 12 people (about NZ$3.30/seat), NZ$59.99/month for up to 30 people, and NZ$99.99/month for up to 50 people. Companies with more than 50 staff can get a custom plan by emailing support@fieldlink.co.nz. Every plan includes timesheets, jobs, safety, fleet, SSSPs and payroll — no feature gating. New companies get a 7-day free trial of the full product with every feature unlocked, no credit card required.",
  },
  {
    cat: "Pricing & trial",
    q: "Is there a free trial?",
    a: "Yes — every new business gets a 7-day free trial of the full Fieldlink suite with every feature unlocked. No credit card required. After the trial you pick a plan or your data stays paused (we don't delete anything for 90 days, so you have time to come back).",
  },
  {
    cat: "Pricing & trial",
    q: "Do you charge extra per field worker?",
    a: "No — our pricing is whole-of-crew. Add as many field workers, office staff and trucks as you want within the seat band. Most NZ contractors find this much cheaper than per-seat tools like Simpro or Tradify when you have 5+ workers.",
  },
  {
    cat: "NZ-specific",
    q: "Is Fieldlink a New Zealand-made app?",
    a: "100% — Fieldlink is designed, built, owned and supported in New Zealand. Data is hosted in the Sydney (ap-southeast-2) region, the closest cloud region to NZ, our support team is in Aotearoa, and the software is built specifically for NZ civil construction (Holidays Act workflows, NZ payroll calendars, NZ supplier names like PlaceMakers, Bunnings, Mitre 10, Carters, ITM).",
  },
  {
    cat: "NZ-specific",
    q: "Does Fieldlink comply with the Holidays Act?",
    a: "Fieldlink is built to support Holidays Act 2003 workflows — our timesheet and pay-run features apply day-of-week / RDP logic for public holidays (including Mondayisation), leave and overtime. We also keep an immutable audit trail of every timesheet edit, so you have complete records if you're ever asked for them.",
  },
  {
    cat: "NZ-specific",
    q: "Where is my data stored?",
    a: "Your data is stored in MongoDB Atlas's Sydney AP-SOUTHEAST-2 region, which is the closest region to NZ. Backups are encrypted and stored within the same region. We do not move data outside Australasia.",
  },
  {
    cat: "Features",
    q: "Does Fieldlink generate SSSPs (Site-Specific Safety Plans)?",
    a: "Yes — Fieldlink supports AI-assisted SSSP drafting, editing and PDF export. Review the site details and content before issuing the document. You can also upload an existing SSSP to a job for worker sign-off.",
  },
  {
    cat: "Features",
    q: "Can I track fleet vehicles and plant?",
    a: "Yes — Fieldlink Fleet tracks utes, diggers, trucks and plant hire by registration. You can record run-hours, fuel, services due (by date or by hours), WOFs, COFs, and link any vehicle to a specific job for cost-per-job tracking.",
  },
  {
    cat: "Features",
    q: "How does the AI invoice scanner work?",
    a: "Office uploads a PDF or photo of a supplier invoice (PlaceMakers slip, Mitre 10 receipt, fuel docket, etc.). Claude Sonnet 4.5 reads the invoice, finds the job number written on it (e.g. J0042), and auto-links the cost to that job. No more manual data entry — the cost shows up instantly in the Cost-per-Job widget.",
  },
  {
    cat: "Features",
    q: "Does Fieldlink work offline on site?",
    a: "Fieldlink is a Progressive Web App (PWA) — field workers install it on their phone like a normal app. The most common screens (timesheet, current job, run-sheet) are cached so workers can view them without signal. New entries sync the moment they're back in range.",
  },
  {
    cat: "Features",
    q: "Can I customise PDFs with my company logo?",
    a: "Yes — upload your company logo in Settings → Branding and it appears on every PDF Fieldlink produces (timesheets, payroll, SSSPs, pay-run reports). PDFs look like your own paperwork, with just a small \"Generated by fieldlink.co.nz\" line in the footer.",
  },
  {
    cat: "Integrations",
    q: "Does it integrate with Xero or MYOB?",
    a: "Yes — Fieldlink connects directly to Xero via official OAuth: timesheets push into Xero Payroll pay runs in one click, and quotes & invoices sync two ways. CSV export is also available for iPayroll and MYOB Payroll.",
  },
  {
    cat: "Integrations",
    q: "Can I send Fieldlink invoices to my accounting software?",
    a: "Yes — once a supplier invoice is scanned and confirmed it can be exported to Xero or MYOB along with the job cost code. The export includes vendor name, invoice number, total, GST split and the linked job's number so reconciliation is one click.",
  },
  {
    cat: "Comparisons",
    q: "How is Fieldlink different from Simpro?",
    a: "Simpro is good for big trade businesses but it's Australian-owned, expensive (often NZ$80+ per seat), and the timesheet UX is built for office staff, not crew on a phone. Fieldlink is NZ-owned, single low monthly fee for the whole crew, and the field-worker app is designed first-and-foremost for one-handed phone use.",
  },
  {
    cat: "Comparisons",
    q: "How is Fieldlink different from Tradify?",
    a: "Tradify focuses on quoting and invoicing for one-person tradies. Fieldlink is built for civil construction crews where the office runs multiple field workers, multiple jobs, and fleet/plant at once. We have dispatch boards, run-sheets, SSSPs and pay runs — Tradify doesn't.",
  },
  {
    cat: "Comparisons",
    q: "Can I import data from Simpro / Tradify / WorkflowMax?",
    a: "Yes — email support@fieldlink.co.nz a CSV export from your old system (client list, job list, employee list) and our team will import it for free, usually within 24 hours. We've done dozens of these migrations.",
  },
  {
    cat: "Support",
    q: "How do I get support?",
    a: "Two ways. (1) In-app: tap Support in the sidebar — opens a ticket that we usually reply to within 2 hours during NZ business hours. (2) Email support@fieldlink.co.nz directly. Calls available for paid customers on request.",
  },
  {
    cat: "Support",
    q: "Do you offer onboarding help?",
    a: "Yes — every new paid customer gets a free 30-minute onboarding call with a real human in NZ. We help you import your team, set up your first jobs and configure pay rules.",
  },
  {
    cat: "Security",
    q: "How is my data secured?",
    a: "Every connection is HTTPS-only with HSTS. Passwords are hashed with bcrypt. JWT tokens expire daily. Files are stored in encrypted object storage. Multi-tenant isolation enforced at every API endpoint — your data never crosses into another company's view.",
  },
  {
    cat: "Security",
    q: "Can I export all my data if I leave?",
    a: "Always — even on the free trial. Settings → Export Data downloads a ZIP with every timesheet, job, client and PDF as CSV/JSON files. No lock-in, ever.",
  },
];

export default function FAQ() {
  const [openIdx, setOpenIdx] = useState(0);
  const [filter, setFilter] = useState("");

  const filtered = filter.trim()
    ? FAQS.filter((f) =>
        (f.q + " " + f.a + " " + f.cat).toLowerCase().includes(filter.toLowerCase()),
      )
    : FAQS;

  // FAQPage JSON-LD — Google may pick individual Q&As and show them as rich snippets
  // in the search results (huge SEO win).
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="bg-[#f6f5f1] text-[#142635] min-h-screen">
      <SEO
        title="FAQ — Fieldlink NZ | Timesheet & Job Management App for NZ Civil Construction"
        description="Common questions about Fieldlink — NZ's home-grown timesheet, jobs, SSSP and fleet app. Pricing, free trial, Holidays Act compliance, Xero / MYOB integration."
        path="/faq"
        jsonLd={jsonLd}
      />

      {/* Nav */}
      <MarketingHeader />

      <section className="bg-[#f6f5f1] text-[#142635] py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="block text-[11px] font-mono font-bold uppercase tracking-[0.28em] text-[#a63c0b] mb-4">
            Frequently asked questions
          </div>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none mb-4">
            Everything you wanted to know
          </h1>
          <p className="text-[#465562] text-base sm:text-lg max-w-2xl mx-auto">
            Pricing, features, integrations, security — answered in plain English. Can&apos;t find what you&apos;re after? <a href="mailto:support@fieldlink.co.nz" className="text-[#a63c0b] underline">Email us</a> and we&apos;ll add the answer.
          </p>
        </div>
      </section>

      {/* Search filter */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
        <div className="relative">
          <Search className="absolute left-3 top-3 w-4 h-4 text-[#52616c]" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search the FAQ…"
            className="w-full h-11 pl-10 pr-4 rounded-lg border border-[#cbd3d5] bg-white text-[#142635] placeholder:text-[#52616c] font-mono text-sm focus:outline-none focus:border-[#FF5F15]"
            data-testid="faq-search"
          />
        </div>

        {/* FAQ items */}
        <div className="mt-8 space-y-2" data-testid="faq-list">
          {filtered.length === 0 && (
            <div className="text-center text-[#52616c] py-8">No matching questions. <a href="mailto:support@fieldlink.co.nz" className="text-[#a63c0b] underline">Ask us</a>?</div>
          )}
          {filtered.map((f, i) => {
            const isOpen = openIdx === i;
            return (
              <div key={f.q} className="rounded-lg border border-[#cbd3d5] bg-white" data-testid={`faq-item-${i}`}>
                <button
                  className="w-full text-left px-5 py-4 flex items-start justify-between gap-4 hover:bg-white"
                  onClick={() => setOpenIdx(isOpen ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  aria-expanded={isOpen}>
                  <div className="min-w-0">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-[#a63c0b] mb-1">{f.cat}</div>
                    <h2 className="font-display font-bold uppercase tracking-tight text-base sm:text-lg leading-tight">{f.q}</h2>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-[#52616c] shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} strokeWidth={2.5} />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 text-sm sm:text-base text-[#465562] leading-relaxed border-t border-[#cbd3d5] pt-4" data-testid={`faq-answer-${i}`}>
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA bottom */}
        <div className="mt-12 rounded-lg border border-[#cbd3d5] bg-white text-[#142635] p-8 text-center">
          <h3 className="font-display font-black uppercase tracking-tight text-2xl mb-2">Ready to give Fieldlink a crack?</h3>
          <p className="text-[#465562] text-sm mb-5">7 days free. Add your whole crew. Cancel anytime. Built in Aotearoa.</p>
          <Link to="/register" className="inline-block rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider px-6 py-3" data-testid="faq-cta-start">
            Start free trial
          </Link>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}

// Shared footer for public pages — now the single global MarketingFooter.
function PublicFooter() {
  return <MarketingFooter />;
}

export { PublicFooter };
